import { Button, useOutsideClick } from '@chakra-ui/react';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import classNames from 'classnames';
import {
  type ChangeEventHandler,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import {
  getChildren,
  getPathToNode,
  Input,
  ThemedSearchIcon,
  type TNode,
  type TreeApi,
  type TreeApiBaseProps,
  type TreeApiControlProps,
  TreeSelection,
  type TreeSelectionComponentProps,
  useTreeApi,
} from '@revelio/ui';

import { isEventInsideEntityInfoPopover } from '../EntityInfoPopover';
import { BranchLabel } from './BranchLabel';
import { FloatingTreePanel } from './FloatingTreePanel';
import styles from './PopoutTree.module.css';
import { useDrillMode } from './useDrillMode';

const DEFAULT_POPOUT_WIDTH = 280;

const COLLAPSE_ALL_HEIGHT = 16;
const BRANCH_CONTAINER_HEIGHT = 19;
const TREE_HEIGHT = 236;

const getHeight = ({
  isSearchEmpty,
  branches,
  showCollapseToggle = true,
}: {
  isSearchEmpty: boolean;
  branches?: PopoutTreeProps['branches'];
  showCollapseToggle?: boolean;
}) => {
  const branchContainerHeight =
    branches && isSearchEmpty ? BRANCH_CONTAINER_HEIGHT : 0;
  const collapseAllTextHeight = showCollapseToggle ? COLLAPSE_ALL_HEIGHT : 0;

  return TREE_HEIGHT - branchContainerHeight - collapseAllTextHeight;
};

type Branch = {
  label: string;
  description?: string;
};

export type PopoutTreeProps = Omit<
  TreeSelectionComponentProps,
  'treeApi' | 'drillMode' | 'onClick'
> &
  Omit<TreeApiBaseProps, 'openByDefault'> &
  TreeApiControlProps & {
    branches?: Branch[] | ((node: TNode) => Branch);
    disabledLevels?: number[];
    searchInputRef?: React.RefObject<HTMLInputElement>;
    openByDefault?: boolean;
    showCollapseToggle?: boolean;
    showSearch?: boolean;
    popoutWidth?: number;
    middleContent?: React.ReactNode;
    /** Override the branch label of the panel at a drill depth (0 is the
     *  root panel). Return `undefined` to fall back to the default
     *  BranchLabel. */
    renderBranchLabel?: (
      branch: Branch,
      drillIndex: number
    ) => React.ReactNode | undefined;
  };

export const PopoutTree = forwardRef(
  (
    {
      branches,
      disabledLevels,
      className,
      searchInputRef,
      openByDefault = false,
      showCollapseToggle = true,
      showSearch = true,
      popoutWidth = DEFAULT_POPOUT_WIDTH,
      middleContent,
      renderBranchLabel,
      ...props
    }: PopoutTreeProps,
    ref: Ref<TreeApi | undefined>
  ) => {
    const { data } = props;
    const treeApi = useTreeApi({ ...props, openByDefault });

    useImperativeHandle(ref, () => treeApi);

    const { search, onSearch, isExpanded, collapse, expand } = treeApi;

    const isSearchEmpty = search.length === 0 && !openByDefault;
    const isDrillEnabled = isSearchEmpty;

    const {
      drillState,
      drillIntoNode,
      drillOutToLevel,
      isDrilledInto,
      clearDrillState,
    } = useDrillMode();

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      onSearch(event.target.value);
    };

    const onResetSearch = () => {
      onSearch('');
    };

    const popoutTreeRef = useRef<HTMLDivElement>(null);
    useOutsideClick({
      ref: popoutTreeRef,
      handler: (event) => {
        // The entity-info tooltip is portaled outside this tree; a click in it
        // (e.g. "View Competitors") isn't a click away, so keep the drill open.
        if (isEventInsideEntityInfoPopover(event)) return;
        clearDrillState();
      },
    });

    const branchLabel = useMemo((): Branch | null => {
      if (typeof branches === 'function') {
        const root = treeApi.treeRef?.current?.root;
        if (root) return branches(root);
        // Fallback: synthesize root-level node (level -1) so the label
        // renders even before react-arborist populates the ref.
        if (data.length > 0) {
          return branches({ level: -1, data: { name: '' } } as TNode);
        }
        return null;
      }
      if (Array.isArray(branches) && branches[0]) {
        return branches[0];
      }
      return null;
    }, [branches, data, treeApi.treeRef]);

    const labelFor = (branch: Branch, drillIndex: number) => {
      const custom = renderBranchLabel?.(branch, drillIndex);
      if (custom !== undefined) return custom;
      return (
        <BranchLabel
          branch={branch.label}
          branchDescription={branch.description}
        />
      );
    };

    const effectiveRootHeight =
      props.height !== undefined
        ? props.height
        : getHeight({ isSearchEmpty, branches, showCollapseToggle });

    const searchGlyph = <ThemedSearchIcon className={styles.searchIcon} />;

    return (
      <div
        className={classNames(styles.popoutTree, className)}
        ref={popoutTreeRef}
      >
        {showSearch && (
          <div
            className={styles.searchGroup}
            onClick={() => {
              clearDrillState();
            }}
          >
            <Input
              size="sm"
              value={search}
              onChange={handleChange}
              placeholder="Search"
              data-testid="tree-search-input"
              autoFocus // not functional inside a popover with the current version of Chakra UI / framer-motion
              ref={searchInputRef}
              className={styles.searchInput}
              wrapperClassName={styles.searchField}
              endSlot={
                isSearchEmpty ? (
                  searchGlyph
                ) : (
                  <IconCrossSmall
                    size={16}
                    className={classNames(styles.searchIcon, styles.resetIcon)}
                    onClick={onResetSearch}
                  />
                )
              }
            />
          </div>
        )}

        {showCollapseToggle && !isSearchEmpty && (
          <Button
            variant="link"
            size="xs"
            colorScheme="gray"
            onClick={isExpanded ? collapse : expand}
          >
            {isExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        )}
        {middleContent && <div>{middleContent}</div>}
        <div
          className={styles.rootTreeContainer}
          data-testid="popout-tree-root"
          data-tree-root
        >
          {isSearchEmpty && branchLabel && labelFor(branchLabel, 0)}
          <TreeSelection
            {...props}
            data={data}
            treeApi={treeApi}
            openByDefault={openByDefault}
            disabled={
              isSearchEmpty ? disabledLevels?.includes(0) : disabledLevels
            }
            height={effectiveRootHeight}
            flattenIndentation={!showCollapseToggle}
            drillMode={{
              isEnabled: isDrillEnabled,
              level: 0,
              isRoot: true,
              drillIntoNode,
              drillOutToLevel,
              ...(isDrillEnabled && { isDrilledInto }),
            }}
            onClick={() => {
              clearDrillState();
            }}
          />
          {drillState.map(({ level, parentNode, anchorRowRect }, i) => {
            const children = getChildren({
              data,
              idPath: getPathToNode(parentNode.id),
            });

            return (
              <FloatingTreePanel
                key={parentNode.id}
                anchorRowRect={anchorRowRect}
                popoutWidth={popoutWidth}
                onClick={() => {
                  drillOutToLevel(i + 1);
                }}
              >
                {(() => {
                  const branch = (() => {
                    if (Array.isArray(branches)) return branches?.[i + 1];
                    if (typeof branches === 'function') {
                      return branches({ ...parentNode, level } as TNode);
                    }
                    return undefined;
                  })();

                  return branch ? labelFor(branch, i + 1) : null;
                })()}
                <TreeSelection
                  {...props}
                  data={children}
                  treeApi={treeApi}
                  openByDefault={openByDefault}
                  disabled={disabledLevels?.includes(i + 1)}
                  flattenIndentation={!showCollapseToggle}
                  drillMode={{
                    isEnabled: isDrillEnabled,
                    level: i + 1,
                    drillIntoNode,
                    drillOutToLevel,
                    ...(isDrillEnabled && { isDrilledInto }),
                  }}
                />
              </FloatingTreePanel>
            );
          })}
        </div>
      </div>
    );
  }
);
